import React, { ReactNode, createContext, useContext } from "react";

/**
 * Per-field config for the IssueDetailPanel.
 *
 * Every field is optional and each flag's default is documented on the flag
 * itself. An absent config produces the standard placement field: non-navigable
 * and not inline-editable, with unlink left up to the user's permission. Flags
 * can only narrow what the user is already permitted to do, never grant beyond
 * it.
 *
 * Only the `placement` field is configurable for now — add other fields (status,
 * assignee, …) here when a real need arrives, not preemptively.
 */
export interface IssueDetailPanelConfig {
  placement?: PlacementFieldConfig;
}

export interface PlacementFieldConfig {
  /**
   * Enhancement flag. When `true`, clicking the placement deeplink navigates to
   * it in the viewer (renders the value outside the disabled InlineEdit button
   * so the click is catchable). Default `false`.
   */
  navigable?: boolean;
  /**
   * Capability flag. `true` (default) leaves removing the placement (the unlink
   * "X") up to the user's permission; `false` force-hides it regardless.
   */
  unlinkable?: boolean;
  /**
   * Default `false` - the placement is not replaceable inline. Set `true` to
   * allow replacing it via the dropdown, subject to the user's permission.
   */
  editable?: boolean;
}

const EMPTY_CONFIG: IssueDetailPanelConfig = {};

export const IssueDetailPanelConfigContext =
  createContext<IssueDetailPanelConfig>(EMPTY_CONFIG);

export const IssueDetailPanelConfigProvider = ({
  config,
  children,
}: {
  config: IssueDetailPanelConfig;
  children: ReactNode;
}) => (
  <IssueDetailPanelConfigContext.Provider value={config}>
    {children}
  </IssueDetailPanelConfigContext.Provider>
);

export const useIssueDetailPanelConfig = (): IssueDetailPanelConfig =>
  useContext(IssueDetailPanelConfigContext);

/** Resolves placement config with its defaults so consumers don't repeat the `?? default` logic. */
export const usePlacementFieldConfig = (): Required<PlacementFieldConfig> => {
  const { placement } = useIssueDetailPanelConfig();
  return {
    navigable: placement?.navigable ?? false,
    unlinkable: placement?.unlinkable ?? true,
    editable: placement?.editable ?? false,
  };
};
